import { LitElement, html, css } from 'lit';
import {
  publishDraftPage, publishDraftSiteSettings, fetchDraftSitemap, fetchPageHistory,
  fetchSiteHistory, resetPageDraft, resetSiteDraft, restorePageVersion,
  restoreSiteVersion, republishPageVersion, republishSiteVersion,
} from '../lib/api.js';

export class SiteDraftPanel extends LitElement {
  static properties = {
    slug: { type: String },
    pageId: { type: Number },
    pageTitle: { type: String },
    pageChanged: { type: Boolean },
    settingsChanged: { type: Boolean },
    pageReady: { type: Boolean },
    _confirmTarget: { state: true },
    _busy: { state: true },
    _message: { state: true },
    _error: { state: true },
    _sitemap: { state: true },
    _history: { state: true },
    _target: { state: true },
  };

  static styles = css`
    :host { position: fixed; right: 1rem; bottom: 1rem; z-index: 10000; font-family: var(--font-sans, system-ui, sans-serif); color: #eef2ff; }
    .panel { width: min(22rem, calc(100vw - 2rem)); padding: 1rem; border: 1px solid rgba(165, 180, 252, .28); border-radius: 1rem; background: rgba(15, 18, 38, .94); box-shadow: 0 1.25rem 4rem rgba(0, 0, 0, .38); backdrop-filter: blur(18px); }
    .eyebrow { margin: 0 0 .3rem; color: #a5b4fc; font-size: .72rem; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
    h2 { margin: 0; font-size: 1.05rem; line-height: 1.25; }
    .status { display: flex; align-items: center; gap: .45rem; margin: .55rem 0 .9rem; color: #cbd5e1; font-size: .82rem; }
    .dot { width: .5rem; height: .5rem; border-radius: 50%; background: #64748b; }
    .dot--changed { background: #fbbf24; box-shadow: 0 0 0 .2rem rgba(251, 191, 36, .14); }
    .actions { display: grid; gap: .55rem; }
    button { appearance: none; border: 0; border-radius: .65rem; padding: .72rem .9rem; font: inherit; font-size: .86rem; font-weight: 750; cursor: pointer; transition: transform 150ms ease, opacity 150ms ease, background 150ms ease; }
    button:hover:not(:disabled) { transform: translateY(-1px); }
    button:disabled { cursor: not-allowed; opacity: .46; }
    .primary { color: #111827; background: #fbbf24; }
    .secondary { color: #e0e7ff; background: rgba(129, 140, 248, .17); border: 1px solid rgba(165, 180, 252, .22); }
    .message { margin: .7rem 0 0; color: #86efac; font-size: .8rem; }
    .error { color: #fca5a5; }
    .overlay { position: fixed; inset: 0; z-index: 10001; display: grid; place-items: center; padding: 1rem; background: rgba(2, 6, 23, .72); backdrop-filter: blur(7px); }
    .dialog { width: min(28rem, calc(100vw - 2rem)); padding: 1.35rem; border: 1px solid rgba(165, 180, 252, .25); border-radius: 1rem; background: #11152d; box-shadow: 0 2rem 6rem rgba(0, 0, 0, .5); }
    .dialog h3 { margin: 0 0 .55rem; font-size: 1.2rem; }
    .dialog p { margin: 0 0 1.15rem; color: #cbd5e1; line-height: 1.55; }
    .dialog-actions { display: flex; justify-content: flex-end; gap: .65rem; }
    .cancel { color: #e2e8f0; background: #29304f; }
    @media (max-width: 540px) { :host { right: .65rem; bottom: .65rem; } .panel { width: calc(100vw - 1.3rem); box-sizing: border-box; } .dialog-actions { flex-direction: column-reverse; } }
    @media (prefers-reduced-motion: reduce) { button { transition: none; } }
  `;

  constructor() {
    super();
    this.slug = 'home';
    this.pageId = null;
    this.pageTitle = 'Home';
    this.pageChanged = false;
    this.settingsChanged = false;
    this.pageReady = false;
    this._confirmTarget = '';
    this._busy = false;
    this._message = '';
    this._error = '';
    this._sitemap = [];
    this._history = [];
    this._target = 'page';
    this._onKeydown = (event) => {
      if (event.key === 'Escape' && !this._busy) this._closeConfirmation();
    };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this._onKeydown);
    this._loadBackoffice();
  }

  updated(changed) {
    if (changed.has('pageId') && this.pageId) this._loadBackoffice();
  }

  async _loadBackoffice() {
    try {
      this._sitemap = await fetchDraftSitemap();
      this._history = this.pageId ? await fetchPageHistory(this.pageId) : [];
    } catch (error) {
      this._error = error?.message || 'Could not load draft management data.';
    }
  }

  _flatten(nodes, depth = 0) {
    return nodes.flatMap(node => [{ ...node, depth }, ...this._flatten(node.children || [], depth + 1)]);
  }

  async _selectTarget(event) {
    const value = event.target.value;
    if (value === 'site') {
      this._target = 'site';
      this._history = await fetchSiteHistory();
      return;
    }
    this._target = 'page';
    const page = this._flatten(this._sitemap).find(item => String(item.id) === value);
    const slug = page?.draft_slug || page?.slug;
    if (slug) window.location.assign(`/drafts/${slug}/`);
  }

  async _resetDraft() {
    if (!window.confirm('Reset this draft to the current published version?')) return;
    if (this._target === 'site') await resetSiteDraft();
    else if (this.pageId) await resetPageDraft(this.pageId);
    else return;
    window.location.reload();
  }

  async _useVersion(versionId) {
    if (this._target === 'site') await restoreSiteVersion(versionId);
    else await restorePageVersion(this.pageId, versionId);
    window.location.reload();
  }

  async _republish(versionId) {
    if (!window.confirm('Publish this historical version as a new publication?')) return;
    if (this._target === 'site') await republishSiteVersion(versionId);
    else await republishPageVersion(this.pageId, versionId);
    await this._loadBackoffice();
    this._message = 'Historical version published as a new version.';
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this._onKeydown);
    super.disconnectedCallback();
  }

  _openConfirmation(target) {
    this._message = '';
    this._error = '';
    this._confirmTarget = target;
  }

  _closeConfirmation() {
    if (!this._busy) this._confirmTarget = '';
  }

  async _publish() {
    const target = this._confirmTarget;
    if (!target || this._busy) return;
    this._busy = true;
    this._error = '';

    try {
      if (target === 'page') {
        await publishDraftPage(this.slug);
        this.pageChanged = false;
        this._message = `${this.pageTitle || this.slug} is now live.`;
      } else {
        await publishDraftSiteSettings();
        this.settingsChanged = false;
        this._message = 'Site settings are now live.';
      }
      this.dispatchEvent(new CustomEvent('draft-published', {
        detail: { target, slug: this.slug },
        bubbles: true,
        composed: true,
      }));
      this._confirmTarget = '';
    } catch (error) {
      this._error = error?.message || 'Publishing failed.';
    } finally {
      this._busy = false;
    }
  }

  render() {
    const anyChanges = this.pageChanged || this.settingsChanged;
    const label = this.pageTitle || this.slug || 'Page';
    const targetLabel = this._confirmTarget === 'site' ? 'site settings' : label;

    return html`
      <aside class="panel" aria-label="Draft publishing controls">
        <p class="eyebrow">Draft preview</p>
        <h2>${label}</h2>
        <label class="eyebrow" for="draft-target">Sitemap</label>
        <select id="draft-target" @change=${this._selectTarget}>
          ${this._flatten(this._sitemap).map(item => html`<option value=${item.id} ?selected=${item.id === 'site' ? this._target === 'site' : this._target === 'page' && String(item.id) === String(this.pageId)}>${'— '.repeat(item.depth || 0)}${item.draft_title || item.title}</option>`)}
        </select>
        <div class="status">
          <span class="dot ${anyChanges ? 'dot--changed' : ''}"></span>
          ${!this.pageReady ? 'Preview unavailable' : anyChanges ? 'Unpublished changes' : 'Everything here is published'}
        </div>
        <div class="actions">
          <button class="primary" ?disabled=${!this.pageReady || !this.pageChanged || this._busy} @click=${() => this._openConfirmation('page')}>
            Publish ${label}
          </button>
          ${this.settingsChanged ? html`
            <button class="secondary" ?disabled=${this._busy} @click=${() => this._openConfirmation('site')}>
              Publish site settings
            </button>
          ` : ''}
          <button class="secondary" ?disabled=${this._target === 'page' && !this.pageId || this._busy} @click=${this._resetDraft}>Reset draft to current publication</button>
        </div>
        <details>
          <summary>Previous publications (${this._history.length})</summary>
          ${this._history.map(version => html`<div class="status">
            <span>v${version.version_number} · ${version.published_at}</span>
            <button class="secondary" @click=${() => this._useVersion(version.id)}>Use as draft</button>
            <button class="primary" @click=${() => this._republish(version.id)}>Publish this version</button>
          </div>`)}
        </details>
        ${this._message ? html`<p class="message" role="status">${this._message}</p>` : ''}
        ${this._error && !this._confirmTarget ? html`<p class="message error" role="alert">${this._error}</p>` : ''}
      </aside>

      ${this._confirmTarget ? html`
        <div class="overlay" role="presentation" @click=${(event) => { if (event.target === event.currentTarget) this._closeConfirmation(); }}>
          <section class="dialog" role="dialog" aria-modal="true" aria-labelledby="publish-title">
            <h3 id="publish-title">Publish ${targetLabel}?</h3>
            <p>This immediately replaces the public version with the draft you are previewing. Publishing is only available here, outside MCP.</p>
            ${this._error ? html`<p class="message error" role="alert">${this._error}</p>` : ''}
            <div class="dialog-actions">
              <button class="cancel" ?disabled=${this._busy} @click=${this._closeConfirmation}>Cancel</button>
              <button class="primary" ?disabled=${this._busy} @click=${this._publish}>
                ${this._busy ? 'Publishing…' : `Publish ${targetLabel}`}
              </button>
            </div>
          </section>
        </div>
      ` : ''}
    `;
  }
}

customElements.define('site-draft-panel', SiteDraftPanel);
